<template>
  <div class="video-box" :style="{ width, height }">
    <video
      class="video-player video-js"
      :id="videoPlayerId"
      webkit-playsinline
      playsinline
      x5-video-player-type="h5-page"
      x5-video-player-fullscreen="false"
      x5-video-orientation="portrait"
      disablePictureInPicture
      controlsList="nodownload noplaybackrate nofullscreen"
    />

    <!-- ==================== 方案一：透明遮罩层 ====================
         只覆盖视频画面区域，底部让出控制条高度，
         保证进度条和按钮可以被正常点击（允许用户后退）。
    =========================================================== -->
    <div
      class="video-mask"
      :style="{ bottom: controlBarHeight + 'px' }"
      v-if="!ifDragSeek && !loadError && !loadLoading && !slideVerifyShow"
      @click="handleMaskClick"
    ></div>

    <div class="video-error" v-if="loadError">
      <div class="video-error-tip">{{ loadMsg }}</div>
      <div class="video-error-action">
        <el-button
          size="mini"
          icon="el-icon-refresh"
          type="primary"
          @click="refreshVideo"
          >重新加载</el-button
        >
      </div>
    </div>

    <div class="video-loading" v-if="loadLoading">
      <div class="video-loading-spring">
        <img src="@/assets/logo.gif" alt="" />
      </div>
    </div>

    <div class="divOut" v-if="slideVerifyShow">
      <slide-verify
        :l="42"
        :r="10"
        :w="350"
        :h="180"
        ref="slideBlock"
        @again="onAgain"
        @success="onSuccess"
        @fail="onFail"
        :slider-text="text"
        :accuracy="3"
        :imgs="images"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "videoPlayer",
  props: {
    focus: { type: Boolean, default: false },
    ifBrush: { type: Boolean, default: false },
    brushTime: { type: Number, default: 1 * 60 },
    moveInPlay: { type: Boolean, default: false },
    width: { type: [Number, String], default: "100%" },
    height: { type: [Number, String], default: "100%" },
    currentUrl: { type: [Number, String], default: 0 },
    ifDragSeek: { type: Boolean, default: false },
    dragTime: { type: Number, default: 0 },
    ifSwitch: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: false },
    muted: { type: Boolean, default: false },
    isToSee: { type: Boolean, default: false },
    isToSeeTime: { type: Number, default: 10 },
    isSwitch: { type: Boolean, default: true },
    ifTabFocus: { type: Boolean, default: true },
    playerList: {
      required: true,
      type: [Array, String],
      default: () => [],
    },
  },
  data() {
    return {
      slideVerifyShow: false,
      toSeeEl: false,
      images: [
        "/static/images/img1.jpg",
        "/static/images/img3.jpg",
        "/static/images/img4.jpg",
        "/static/images/img5.jpg",
        "/static/images/img6.webp",
        "/static/images/img7.webp",
      ],
      loadError: false,
      loadLoading: true,
      loadMsg: "",
      videoPlayerId: "video-player" + Math.ceil(Math.random() * 1000),
      playerUrl: undefined,
      playerEl: null,
      currentPlayerIndex: 0,
      // 已学到的最大位置，单调递增，永不回退
      seekAllow: 0,
      fullscreenSeekAllow: 0,
      slideTime: 0,
      text: "休息一下，向右滑动，恢复播放",
      slideInterval: null,
      tooltipComponent: null,
      docKeydownHandler: null,
      // 方案二：进度校正标志位，避免递归触发
      isCorrecting: false,
      // 遮罩层底部偏移：动态获取控制条高度
      controlBarHeight: 40,
      // ✅ 用于缓存"playerEl / duration 还没就绪时"传入的学习位置
      pendingSeekTime: 0,
    };
  },
  created() {
    this.customComponent();
    this.playerVideoInit();
    if (this.ifBrush) this.slideCountDown();
    if (this.ifTabFocus) {
      document.addEventListener(
        "visibilitychange",
        this.tabVisibilitychange,
        false
      );
    }
  },
  watch: {
    playerList() {
      this.playerVideoInit();
    },
    currentUrl() {
      this.slideCountDown();
    },
  },
  methods: {
    // ==================== 方案一：遮罩层点击 ====================
    handleMaskClick() {
      if (!this.playerEl) return;
      if (this.playerEl.paused()) {
        this.playerEl.play();
      } else {
        this.playerEl.pause();
      }
    },

    // ==================== 对外暴露：设置已学习位置 ====================
    /**
     * 父组件通过 ref 调用：
     *   this.$refs.videoPlayer.setSeekAllow(seconds)
     *
     * 内部自动处理：
     *   - playerEl 未就绪 → 缓存到 pendingSeekTime，等 loadedmetadata 应用
     *   - duration 为 0（元数据未加载）→ 缓存
     *   - 就绪 → 同步 seekAllow + currentTime
     */
    setSeekAllow(time) {
      const t = Number(time) || 0;
      if (t <= 0) return;

      // playerEl 未就绪，缓存
      if (!this.playerEl) {
        this.pendingSeekTime = t;
        return;
      }
      const duration = this.playerEl.duration() || 0;
      // 元数据未加载，缓存
      if (!duration) {
        this.pendingSeekTime = t;
        return;
      }

      const target = Math.min(Math.max(0, t), duration);
      if (!this.ifDragSeek) {
        this.seekAllow = Math.max(this.seekAllow, target);
      }
      this.isCorrecting = false;
      this.playerEl.currentTime(target);
      this.pendingSeekTime = 0;
    },

    // 应用缓存的 pendingSeekTime（在 loadedmetadata 里调）
    _applyPendingSeekTime() {
      if (this.pendingSeekTime > 0 && this.playerEl) {
        const t = this.pendingSeekTime;
        this.pendingSeekTime = 0;
        this.setSeekAllow(t);
      }
    },

    playerVideoInit() {
      if (typeof this.playerList !== "string") {
        if (typeof this.currentUrl == "number") {
          this.currentPlayerIndex = this.currentUrl;
        } else {
          this.currentPlayerIndex = this.playerList.findIndex(
            (item) => item.url === this.currentUrl
          );
        }
        if (this.currentPlayerIndex === -1) {
          this.$message.error("视频地址错误，请检查地址");
          return false;
        }
        this.playerUrl = this.playerList[this.currentPlayerIndex].url;
      } else {
        this.playerUrl = this.playerList;
      }
      this.$nextTick(() => {
        this.playerInit();
      });
    },

    playerInit() {
      const that = this;
      if (that.playerEl) {
        try {
          that.playerEl.dispose();
        } catch (e) {}
        that.playerEl = null;
      }

      that.playerEl = window.videojs(
        that.videoPlayerId,
        {
          width: that.width,
          height: that.height,
          controls: true,
          playbackRates: [],
          autoplay: that.autoplay,
          language: "zh-CN",
          muted: that.muted,
          controlBar: {
            volumePanel: { inline: false },
            children: [
              { name: "playToggle" },
              { name: "currentTimeDisplay" },
              { name: "MyCustomSeekBar" },
              { name: "durationDisplay" },
              { name: "volumePanel" },
              { name: "fullscreenToggle" },
            ],
          },
          sources: [{ src: that.playerUrl, type: "video/mp4" }],
        },
        function onPlayerReady() {
          this.player_.playsinline(true);

          const applyPiPDisabled = () => {
            const videoEl = this.player_.el();
            if (videoEl) {
              videoEl.disablePictureInPicture = true;
              videoEl.setAttribute("disablePictureInPicture", "true");
              videoEl.removeAttribute("controlsList");
              videoEl.setAttribute(
                "controlsList",
                "nodownload noplaybackrate nofullscreen"
              );
            }
          };
          applyPiPDisabled();
          this.on("enterpictureinpicture", function () {
            if (document.exitPictureInPicture) document.exitPictureInPicture();
          });

          that.tooltipComponent = this.addChild("MyCustomTip");

          const measureControlBar = () => {
            const cb = this.player_.el().querySelector(".vjs-control-bar");
            if (cb && cb.offsetHeight > 0) {
              that.controlBarHeight = cb.offsetHeight;
            }
          };
          that.$nextTick(measureControlBar);
          setTimeout(measureControlBar, 300);
          setTimeout(measureControlBar, 1000);

          if (that.focus) {
            this.on("mouseleave", function () {
              if (!this.player_.paused()) {
                that.tooltipComponent.showTooltip("鼠标移除视频将会暂停！");
                this.player_.pause();
              }
            });
          }
          if (that.moveInPlay) {
            this.on("mouseenter", function () {
              if (this.player_.paused()) this.player_.play();
            });
          }

          this.on("contextmenu", function (event) {
            event.preventDefault();
          });

          this.on("keydown", function (e) {
            if (that.ifDragSeek) return;
            const k = e.key;
            if (k === "ArrowRight" || k === "l" || k === "L") {
              e.preventDefault();
              e.stopPropagation();
              that.tooltipComponent.showTooltip(
                "您还未学习到该位置，还不能快进"
              );
            }
          });

          that.docKeydownHandler = function (e) {
            if (that.ifDragSeek) return;
            const k = e.key;
            if (k === "ArrowRight" || k === "l" || k === "L") {
              e.preventDefault();
              e.stopPropagation();
              if (that.tooltipComponent) {
                that.tooltipComponent.showTooltip(
                  "您还未学习到该位置，还不能快进"
                );
              }
            }
          };
          document.addEventListener("keydown", that.docKeydownHandler, true);

          this.on("loadstart", function () {
            that.loadError = false;
            that.loadLoading = true;
            that.loadstart(this);
          });
          this.on("progress", function () {});

          // ✅ 元数据加载完成后：优先应用缓存的 pendingSeekTime
          this.on("loadedmetadata", function () {
            that.loadLoading = false;
            applyPiPDisabled();
            measureControlBar();

            that.$nextTick(() => {
              that._applyPendingSeekTime();
            });
          });

          this.on("canplaythrough", function () {});
          this.on("waiting", function () {});

          this.on("play", function () {
            that.playerEl.playbackRate(1);
            that.play(this);
          });
          this.on("playing", function () {
            that.playing(this);
          });

          this.on("pause", function () {
            if (
              that.isToSee &&
              Math.floor(this.currentTime()) >= that.isToSeeTime
            ) {
              that.playerEl.pause();
              that.tooltipComponent.showTooltip("试看结束！");
            }
            that.pause(this);
          });

          this.on("ended", function () {
            if (
              !that.playerEl.isFullscreen() ||
              that.fullscreenSeekAllow + 60 > that.playerEl.duration() ||
              that.ifDragSeek
            ) {
              if (that.isSwitch) that.playerChange();
              that.ended(this);
            }
          });

          this.on("error", function () {
            that.loadMsg = this.error_ && this.error_.message;
            that.loadError = true;
            that.loadLoading = false;
            if (this.errorDisplay) this.errorDisplay.close();
          });

          // ============ 方案二：seeking 兜底拦截 ============
          this.on("seeking", function () {
            const cur = this.currentTime();
            if (that.isCorrecting) {
              that.seeking(this);
              return;
            }
            if (!that.ifDragSeek && cur > that.seekAllow + 2) {
              that.isCorrecting = true;
              that.tooltipComponent.showTooltip(
                "您还未学习到该位置，还不能快进"
              );
              this.currentTime(that.seekAllow);
              setTimeout(() => {
                that.isCorrecting = false;
              }, 100);
            }
            that.seeking(this);
          });

          // ============ seeked：试看限制 + 方案二校正 ============
          this.on("seeked", function () {
            const cur = this.currentTime();
            if (that.isToSee && Math.floor(cur) > that.isToSeeTime) {
              that.playerEl.pause();
              that.playerEl.currentTime(that.isToSeeTime);
              return;
            }
            if (
              !that.isCorrecting &&
              !that.ifDragSeek &&
              cur > that.seekAllow + 2
            ) {
              that.isCorrecting = true;
              that.tooltipComponent.showTooltip(
                "您还未学习到该位置，还不能快进"
              );
              this.currentTime(that.seekAllow);
              setTimeout(() => {
                that.isCorrecting = false;
              }, 100);
              return;
            }
            if (!that.ifDragSeek && cur > that.seekAllow) {
              that.seekAllow = cur;
            }
            that.seeked(this);
          });

          this.on("ratechange", function () {
            if (this.playbackRate() !== 1) this.playbackRate(1);
          });

          // ============ timeupdate：只记录 + 方案二兜底 ============
          this.on("timeupdate", function () {
            const cur = this.currentTime();

            if (that.isToSee && Math.floor(cur) >= that.isToSeeTime) {
              that.playerEl.pause();
              that.toSeeEl = true;
            }

            if (!that.ifDragSeek) {
              if (that.isCorrecting) {
                that.timeupdate(this);
                return;
              }
              if (cur > that.seekAllow + 2) {
                that.isCorrecting = true;
                that.tooltipComponent.showTooltip(
                  "您还未学习到该位置，还不能快进"
                );
                this.currentTime(that.seekAllow);
                setTimeout(() => {
                  that.isCorrecting = false;
                }, 100);
                that.timeupdate(this);
                return;
              }
              if (cur > that.seekAllow) that.seekAllow = cur;
            } else {
              window.initialTime = null;
            }

            that.timeupdate(this);
          });

          this.on("volumechange", function () {});
          this.on("stalled", function () {});
        }
      );
    },

    slideCountDown() {
      const that = this;
      if (that.slideInterval) {
        clearInterval(that.slideInterval);
        that.slideInterval = null;
      }
      that.slideTime = that.brushTime;
      that.slideInterval = setInterval(() => {
        if (that.playerEl && !that.playerEl.paused()) {
          if (that.slideTime <= 0) {
            if (that.playerEl.isFullscreen()) that.exitFullscreen();
            that.slideVerifyShow = true;
            that.playerEl.pause();
            that.slideTime = that.brushTime;
            clearInterval(that.slideInterval);
            that.slideInterval = null;
          } else {
            that.slideTime--;
          }
        }
      }, 1000);
    },

    exitFullscreen() {
      if (document.exitFullScreen) document.exitFullScreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
      return true;
    },

    playerChange() {
      if (this.currentPlayerIndex === this.playerList.length - 1) {
        this.$emit("overEnd");
        return false;
      }
      this.currentPlayerIndex++;
      if (this.ifSwitch) {
        this.seekAllow = 0;
        this.playerEl.src(this.playerList[this.currentPlayerIndex].url);
        this.refreshVideo();
        this.$emit("switch", {
          index: this.currentPlayerIndex,
          playerList: this.playerList,
        });
      }
    },

    playerSwitch(index) {
      this.currentPlayerIndex = index;
      this.seekAllow = 0;
      this.playerEl.src(this.playerList[this.currentPlayerIndex].url);
      this.refreshVideo();
    },

    loadstart() { this.$emit("loadStart", arguments[0]); },
    pause() { this.$emit("pause", arguments[0]); },
    play() { this.$emit("play", arguments[0]); },
    playing() { this.$emit("playing", arguments[0]); },
    ended() { this.$emit("ended", arguments[0]); },

    // ✅ timeupdate emit 两个参数：player 实例 + seekAllow
    timeupdate() {
      this.$emit("timeupdate", arguments[0], this.seekAllow);
    },

    seeking() { this.$emit("seeking", arguments[0]); },
    seeked() { this.$emit("seeked", arguments[0]); },

    // ✅ 对外暴露：获取已学到的最大位置
    getSeekAllow() {
      return this.seekAllow;
    },

    setSeek(currentTime) {
      if (this.playerEl) this.playerEl.currentTime(currentTime);
    },

    refreshVideo() {
      if (this.slideInterval) {
        clearInterval(this.slideInterval);
        this.slideInterval = null;
      }
      if (this.playerEl) {
        if (this.playerEl.isFullscreen()) this.exitFullscreen();
        this.playerEl.load();
      }
    },

    onSuccess(times) {
      this.$emit("onSuccess");
      this.slideVerifyShow = false;
      this.slideCountDown();
      this.playerEl.play();
      console.log("验证通过，耗时 " + times + "毫秒");
    },
    onFail() {
      this.$emit("onFail");
      console.log("验证不通过");
    },
    onAgain() {
      console.log("检测到非人为操作的哦！");
      this.$emit("onAgain");
      this.msg = "再试一次";
      this.$refs.slideBlock.reset();
    },
    handleClick() {
      this.$refs.slideBlock.reset();
    },

    // ==================== 自定义组件 ====================
    customComponent() {
      const that = this;

      // ---------- 自定义进度条 ----------
      class MyCustomSeekBar extends window.videojs.getComponent("SeekBar") {
        constructor(player, options) {
          super(player, options);
          this.userSeek_ = false;
          this._onDocMove = (e) => this.handleMouseMove(e);
          this._onDocUp = (e) => this.handleMouseUp(e);

          this.on("keydown", function (e) {
            if (that.ifDragSeek) return;
            const k = e.key;
            if (k === "ArrowRight" || k === "l" || k === "L") {
              e.preventDefault();
              e.stopPropagation();
              that.tooltipComponent.showTooltip(
                "您还未学习到该位置，还不能快进"
              );
            }
          });
        }

        handleMouseDown(event) {
          if (event.type === "mousedown") event.preventDefault();
          this.userSeek_ = true;
          this.update();

          const doc = this.el_.ownerDocument;
          doc.addEventListener("mousemove", this._onDocMove, true);
          doc.addEventListener("mouseup", this._onDocUp, true);
          doc.addEventListener("touchmove", this._onDocMove, true);
          doc.addEventListener("touchend", this._onDocUp, true);
        }

        handleMouseMove() {
          if (!this.userSeek_) return;
          this.update();
        }

        handleMouseUp(event) {
          const doc = this.el_.ownerDocument;
          doc.removeEventListener("mousemove", this._onDocMove, true);
          doc.removeEventListener("mouseup", this._onDocUp, true);
          doc.removeEventListener("touchmove", this._onDocMove, true);
          doc.removeEventListener("touchend", this._onDocUp, true);

          if (!this.userSeek_) return;
          this.userSeek_ = false;

          if (!event) return;
          const pageX =
            event.pageX != null
              ? event.pageX
              : event.changedTouches && event.changedTouches[0]
              ? event.changedTouches[0].pageX
              : null;
          if (pageX == null) return;

          const rect = this.el_.getBoundingClientRect();
          if (!rect.width) return;
          const percent = Math.max(
            0,
            Math.min(1, (pageX - rect.left) / rect.width)
          );
          const duration = this.player_.duration() || 0;
          if (!duration) return;
          const time = percent * duration;

          if (that.ifDragSeek) {
            this.player_.currentTime(time);
            return;
          }

          const beforeTime = this.player_.currentTime();
          if (that.seekAllow < beforeTime) that.seekAllow = beforeTime;

          if (that.isToSee && time > that.isToSeeTime) {
            that.tooltipComponent.showTooltip("试看结束");
            this.player_.currentTime(that.isToSeeTime);
            this.player_.pause();
            return;
          }

          // time <= seekAllow + 1 → 允许（含后退）；否则拦截
          if (time > that.seekAllow + 1) {
            that.tooltipComponent.showTooltip(
              "您还未学习到该位置，还不能跳转"
            );
            this.player_.currentTime(beforeTime);
          } else {
            this.player_.currentTime(time);
          }
        }

        dispose() {
          const doc = this.el_ && this.el_.ownerDocument;
          if (doc) {
            doc.removeEventListener("mousemove", this._onDocMove, true);
            doc.removeEventListener("mouseup", this._onDocUp, true);
            doc.removeEventListener("touchmove", this._onDocMove, true);
            doc.removeEventListener("touchend", this._onDocUp, true);
          }
          super.dispose();
        }
      }
      window.videojs.registerComponent("MyCustomSeekBar", MyCustomSeekBar);

      // ---------- 自定义提示 ----------
      class MyCustomTip extends window.videojs.getComponent("Component") {
        constructor(player, options) {
          super(player, options);
          this.hideTooltipTimeout = null;
          this.hideTooltip();
        }
        createEl() {
          return super.createEl("div", {
            className: "vjs-tooltip",
            textContent: "",
            style: { display: "none", opacity: 0 },
          });
        }
        showTooltip(message) {
          this.el_.textContent = "温馨提示：" + message;
          this.el_.style.display = "block";
          this.el_.style.opacity = 1;
          if (this.hideTooltipTimeout) clearTimeout(this.hideTooltipTimeout);
          this.hideTooltipTimeout = setTimeout(() => {
            this.hideTooltip();
          }, 3000);
        }
        hideTooltip() {
          this.el_.style.display = "none";
          this.el_.textContent = "";
          this.el_.style.opacity = 0;
        }
        dispose() {
          clearTimeout(this.hideTooltipTimeout);
          super.dispose();
        }
      }
      window.videojs.registerComponent("MyCustomTip", MyCustomTip);
    },

    tabVisibilitychange() {
      if (this.playerEl) {
        if (document.visibilityState === "hidden" && !this.playerEl.paused()) {
          this.playerEl.pause();
          this.tooltipComponent.showTooltip("您离开该页面将会停止播放");
        }
      }
    },
  },

  beforeDestroy() {
    if (this.playerEl) {
      try {
        this.playerEl.dispose();
      } catch (e) {}
      this.playerEl = null;
    }
    if (this.ifTabFocus) {
      document.removeEventListener(
        "visibilitychange",
        this.tabVisibilitychange
      );
    }
    if (this.docKeydownHandler) {
      document.removeEventListener("keydown", this.docKeydownHandler, true);
      this.docKeydownHandler = null;
    }
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = null;
    }
  },
};
</script>

<style scoped lang="scss">
.video-box {
  position: relative;
  height: 100%;
  width: 100%;

  .video-player {
    position: relative;
    width: 100%;
    height: 100%;
    ::v-deep .vjs-duration {
      display: block;
    }
    ::v-deep .vjs-current-time {
      display: block;
    }
  }

  .video-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    /* bottom 由 :style 动态设置 */
    z-index: 2;
    background: transparent;
    cursor: pointer;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
  }

  ::v-deep .vjs-control-bar {
    z-index: 3 !important;
  }

  .video-error {
    position: absolute;
    color: #ffffff;
    background-color: #000000;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    padding: 10px;
    z-index: 10;
    box-sizing: border-box;
    .video-error-tip {
      text-align: center;
      margin-top: 10%;
    }
    .video-error-action {
      margin-top: 20px;
      text-align: center;
    }
  }

  .video-loading {
    position: absolute;
    color: #ffffff;
    background-color: #000000;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    padding: 10px;
    z-index: 10;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .video-loading-spring {
    position: relative;
    width: 70px;
    height: 70px;
    margin-bottom: 80px;
    &:after {
      position: absolute;
      top: 0;
      left: 0;
      content: "";
      width: 70px;
      height: 70px;
      border: 5px solid #ffffff;
      border-top-color: transparent;
      border-radius: 100%;
      animation: circle infinite 0.75s linear;
    }
    img {
      width: 60px;
      height: 60px;
      position: absolute;
      top: 10px;
      left: 10px;
    }
  }
}

.divOut {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 11;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.9);
  ::v-deep .slide-verify {
    background-color: #ffffff;
    .slide-verify-slider {
      width: calc(100% - 1px);
      margin-top: 0;
    }
  }
}

@keyframes circle {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}

::v-deep .vjs-picture-in-picture-control,
::v-deep .vjs-playback-rate {
  display: none !important;
}
</style>